/**
 * VersicherteDashboardPage - Versicherten portal dashboard
 * Nordic Sanctuary design: Calm, reassuring, clear actions
 *
 * Shows greeting, open invitations, upcoming and past appointments.
 * Shortcut: if exactly 1 invitation and 0 appointments, redirect to booking.
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { api } from "@jourfx/convex/client";
import {
  CalendarPlus,
  Clock,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  LogOut,
  Inbox,
} from "lucide-react";
import { Button } from "@jourfx/ui";
import { ClientLayout } from "../components/layout/ClientLayout";
import { useVersichertenSession } from "../hooks/useVersichertenSession";

// ---------------------------------------------------------------------------
// Status badge component
// ---------------------------------------------------------------------------

const statusColorMap: Record<string, string> = {
  scheduled:
    "bg-[var(--sage-100)] text-[var(--sage-700)] border-[var(--sage-300)]",
  confirmed:
    "bg-[oklch(0.93_0.05_155)] text-[oklch(0.35_0.10_155)] border-[oklch(0.80_0.08_155)]",
  completed:
    "bg-[var(--stone-100)] text-[var(--stone-600)] border-[var(--stone-300)]",
  cancelled:
    "bg-[oklch(0.95_0.03_25)] text-[oklch(0.45_0.12_25)] border-[oklch(0.85_0.06_25)]",
};

function AppointmentStatusBadge({
  status,
  label,
}: {
  status: string;
  label: string;
}) {
  const colors =
    statusColorMap[status] ??
    "bg-[var(--stone-100)] text-[var(--stone-600)] border-[var(--stone-300)]";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${colors}`}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function VersicherteDashboardPage() {
  const { t, i18n } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { session, isValid, isLoading, clearSession } = useVersichertenSession();
  const [showPast, setShowPast] = useState(false);

  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "de-DE";
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
    [locale]
  );
  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
      }),
    [locale]
  );

  const formatDate = (timestamp: number) =>
    dateFormatter.format(new Date(timestamp));
  const formatTime = (timestamp: number) =>
    timeFormatter.format(new Date(timestamp));

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "scheduled":
        return t("status.scheduled");
      case "confirmed":
        return t("status.confirmed");
      case "completed":
        return t("status.completed");
      case "cancelled":
        return t("status.cancelled");
      default:
        return status;
    }
  };

  const apiRef = api as any;
  const dashboard = useQuery(
    apiRef.versichertenProfiles.index.getDashboard,
    session?.sessionToken ? { sessionToken: session.sessionToken } : "skip"
  );

  // Redirect to login if no valid session
  useEffect(() => {
    if (!isLoading && !isValid) {
      void navigate({ to: "/" });
    }
  }, [isValid, isLoading, navigate]);

  // Shortcut: 1 invitation + 0 appointments -> go directly to booking
  useEffect(() => {
    if (!dashboard || !isValid) return;
    if (
      dashboard.openInvitations.length === 1 &&
      dashboard.upcomingAppointments.length === 0 &&
      dashboard.pastAppointments.length === 0
    ) {
      void navigate({
        to: "/booking/$invitationId",
        params: { invitationId: dashboard.openInvitations[0]._id },
      } as any);
    }
  }, [dashboard, navigate]);

  // Logout handler
  const handleLogout = () => {
    clearSession();
    void navigate({ to: "/" });
  };

  // ---------- Loading state ----------
  if (isLoading || dashboard === undefined) {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-8 text-center">
          <div className="client-spinner mx-auto mb-4" aria-hidden="true" />
          <p className="text-client-muted font-medium">{t("loading")}</p>
        </div>
      </ClientLayout>
    );
  }

  // ---------- Derived data ----------
  const versicherter = dashboard.versicherter;
  const { openInvitations, upcomingAppointments, pastAppointments } = dashboard;
  const hasContent =
    openInvitations.length > 0 ||
    upcomingAppointments.length > 0 ||
    pastAppointments.length > 0;

  const greeting = versicherter.anrede
    ? t("greeting", { anrede: versicherter.anrede, lastName: versicherter.lastName })
    : t("greetingNoAnrede", {
        firstName: versicherter.firstName,
        lastName: versicherter.lastName,
      });

  return (
    <ClientLayout>
      <div className="w-full max-w-md space-y-5">
        {/* ---- Greeting header ---- */}
        <div className="client-card overflow-hidden">
          <div className="bg-gradient-to-br from-[var(--sage-500)] to-[var(--sage-600)] p-6 text-white text-center relative overflow-hidden">
            {/* Decorative circles */}
            <div
              className="absolute -left-12 -top-12 w-40 h-40 rounded-full bg-white/10"
              aria-hidden="true"
            />
            <div
              className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/10"
              aria-hidden="true"
            />
            <h1 className="relative text-xl font-semibold">{greeting}</h1>
          </div>
        </div>

        {/* ---- Empty state ---- */}
        {!hasContent && (
          <div className="client-card p-8 text-center space-y-3">
            <Inbox
              className="w-12 h-12 mx-auto text-[var(--stone-400)]"
              aria-hidden="true"
            />
            <h2 className="text-lg font-semibold text-[var(--stone-700)]">
              {t("empty.title")}
            </h2>
            <p className="text-sm text-[var(--stone-500)]">
              {t("empty.message")}
            </p>
          </div>
        )}

        {/* ---- Open Invitations ---- */}
        {openInvitations.length > 0 && (
          <section aria-labelledby="invitations-heading">
            <h2
              id="invitations-heading"
              className="text-sm font-semibold uppercase tracking-wider text-[var(--stone-500)] mb-2 px-1"
            >
              {t("invitations.title")}
            </h2>
            <ul className="space-y-3">
              {openInvitations.map(
                (inv: {
                  _id: string;
                  gutachterName: string;
                  duration: number;
                  expiresAt: number;
                }) => (
                  <li key={inv._id} className="client-card p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[var(--stone-700)] truncate">
                          {inv.gutachterName}
                        </p>
                        <p className="text-sm text-[var(--stone-500)] flex items-center gap-1.5 mt-0.5">
                          <Clock
                            className="w-3.5 h-3.5 flex-shrink-0"
                            aria-hidden="true"
                          />
                          {t("invitations.duration", {
                            duration: inv.duration,
                          })}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate({
                            to: "/booking/$invitationId",
                            params: { invitationId: inv._id },
                          } as any)
                        }
                      >
                        <CalendarPlus
                          className="w-4 h-4"
                          aria-hidden="true"
                        />
                        {t("invitations.bookButton")}
                      </Button>
                    </div>
                  </li>
                )
              )}
            </ul>
          </section>
        )}

        {/* ---- Upcoming Appointments ---- */}
        {upcomingAppointments.length > 0 && (
          <section aria-labelledby="upcoming-heading">
            <h2
              id="upcoming-heading"
              className="text-sm font-semibold uppercase tracking-wider text-[var(--stone-500)] mb-2 px-1"
            >
              {t("upcoming.title")}
            </h2>
            <ul className="space-y-3">
              {upcomingAppointments.map(
                (apt: {
                  _id: string;
                  scheduledAt: number;
                  duration: number;
                  status: string;
                  gutachterName: string;
                }) => (
                  <li key={apt._id} className="client-card p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-[var(--stone-700)]">
                            {formatDate(apt.scheduledAt)}
                          </p>
                          <AppointmentStatusBadge
                            status={apt.status}
                            label={getStatusLabel(apt.status)}
                          />
                        </div>
                        <p className="text-sm text-[var(--stone-500)]">
                          {formatTime(apt.scheduledAt)} &middot;{" "}
                          {apt.gutachterName}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          navigate({
                            to: "/appointment/$appointmentId",
                            params: { appointmentId: apt._id },
                          } as any)
                        }
                        aria-label={`${t("upcoming.viewDetails")} – ${formatDate(apt.scheduledAt)}`}
                      >
                        <ChevronRight
                          className="w-5 h-5"
                          aria-hidden="true"
                        />
                      </Button>
                    </div>
                  </li>
                )
              )}
            </ul>
          </section>
        )}

        {/* ---- Past Appointments (collapsible) ---- */}
        {pastAppointments.length > 0 && (
          <section aria-labelledby="past-heading">
            <button
              id="past-heading"
              type="button"
              className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[var(--stone-500)] mb-2 px-1 hover:text-[var(--stone-700)] transition-colors"
              onClick={() => setShowPast((prev) => !prev)}
              aria-expanded={showPast}
            >
              {showPast ? (
                <ChevronUp className="w-4 h-4" aria-hidden="true" />
              ) : (
                <ChevronDown className="w-4 h-4" aria-hidden="true" />
              )}
              {showPast ? t("past.hide") : t("past.show")}
            </button>

            {showPast && (
              <ul className="space-y-2">
                {pastAppointments.map(
                  (apt: {
                    _id: string;
                    scheduledAt: number;
                    duration: number;
                    status: string;
                    gutachterName: string;
                  }) => (
                    <li
                      key={apt._id}
                      className="client-card p-3 opacity-80"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-[var(--stone-600)]">
                              {formatDate(apt.scheduledAt)}
                            </p>
                            <AppointmentStatusBadge
                              status={apt.status}
                              label={getStatusLabel(apt.status)}
                            />
                          </div>
                          <p className="text-xs text-[var(--stone-400)] mt-0.5">
                            {apt.gutachterName}
                          </p>
                        </div>
                      </div>
                    </li>
                  )
                )}
              </ul>
            )}
          </section>
        )}

        {/* ---- Logout ---- */}
        <div className="pt-2">
          <Button
            variant="ghost"
            className="w-full text-[var(--stone-500)] hover:text-[var(--stone-700)]"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            {t("logout")}
          </Button>
        </div>
      </div>
    </ClientLayout>
  );
}
